/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { useQuery, useMutation } from '@apollo/client';
import isEmpty from 'lodash/isEmpty';
import head from 'lodash/head';
import last from 'lodash/last';
import flatten from 'lodash/flatten';

import { C, F } from '../constants/theme';
import { Btn, Callout, Icon, Sub, Heading, LoadingScreen } from '../components';
import { useDeliveryDays } from '../../UserManagementPage/hooks';
import { GET_PROGRAM_BUNDLE } from 'core/api/apollo/queries';
import { CREATE_ONE_TIME_PROGRAM, CREATE_SPLIT_TEST_ASSIGNMENT } from 'core/api/apollo/mutations';
import { SanitizedHTML } from 'core/ui/atoms';
import useListPrograms from 'core/hooks/useListPrograms';
import { useProgramContext } from 'core/api/programContext';
import { updateUserQuery } from 'core/api/cache/updaters';
import useErrorHandlers from 'core/hooks/useErrorHandlers';
import { BackgroundJobStatusCheck } from 'core/ui/components';

const Phase6PlanConfig = ({
  dots,
  goPhase,
}) => {
  const [loading, setLoading] = useState(false);
  const { firstPeriod } = useDeliveryDays();
  const {
    backgroundJobId,
    setBackgroundJobId,
    bundleId,
    oneTimeProgramId,
    setOneTimeProgramId,
    setBundleId,
    selectedDiet,
    selectedProgram,
    programSize,
    mealBundle,
    setMealBundle,
    daysOfFood,
    setDaysOfFood,
    totalAmount,
    setTotalAmount,
    goals,
    cuisines,
    proteins,
    avoidances,
    flagCount,
    household,
    sizeLabel,
    specs,
    // eslint-disable-next-line no-unused-vars
    bundleLabel,
    // eslint-disable-next-line no-unused-vars
    daysLabel,
  } = useProgramContext();

  const { programMealTypesList, programSchedules, daysOfFoodList, blSignatureProgramList } = useListPrograms();

  // eslint-disable-next-line no-console
  console.log('firstPeriod', firstPeriod);

  const isMealNoDinner = mealBundle?.name === 'Without Dinner';
  const resolvedProgramId = (() => {
    if (isMealNoDinner && selectedDiet && blSignatureProgramList?.length) {
      const blMatch = blSignatureProgramList.find(
        (p) => p.internalName === selectedDiet.internalName
      );
      return blMatch?.id || selectedDiet?.id;
    }
    return selectedDiet?.id;
  })();

  // eslint-disable-next-line no-unused-vars
  const { loading: loadingProgramBundle } = useQuery(GET_PROGRAM_BUNDLE, {
    variables: {
      programId: resolvedProgramId,
      scheduleId: daysOfFood?.id,
      sizeId: programSize?.id,
      mealTypeId: mealBundle?.id,
      numWeeks: 1,
      otpSubscribed: true,
      deliveryDayId: firstPeriod?.id,
    },
    skip:
      !isEmpty(bundleId) ||
      isEmpty(mealBundle?.id) ||
      isEmpty(selectedDiet?.id) ||
      isEmpty(daysOfFood?.id),
    fetchPolicy: 'network-only',
    onCompleted: (response) => {
      const bundleIdResponse =
        response && response.getProgramBundle ? response.getProgramBundle.id : null;
      const bundlePrice = response?.getProgramBundle?.price;
      // eslint-disable-next-line no-unused-vars
      // onSubmitOneTimeProgram(bundleId);
      // eslint-disable-next-line no-console
      console.log('response: bundleId', bundleIdResponse);
      setBundleId(bundleIdResponse);
      setTotalAmount(bundlePrice);
    },
    notifyOnNetworkStatusChange: true,
  });

  const { displayApolloResponseErrors } = useErrorHandlers();

  // eslint-disable-next-line no-console
  console.log('totalAmount', totalAmount);

  // eslint-disable-next-line no-unused-vars
  const [createOneTimeProgram, { loading: loadingCreateOneTimeProgram }] = useMutation(CREATE_ONE_TIME_PROGRAM, {
    update: (
      cache,
      {
        data: {
          createOneTimeProgram: { oneTimeProgram, errors },
        },
      },
    ) => {
      updateUserQuery(
        cache,
        { user: { nextOneTimeProgram: { bundleReservations: null } } },
        errors,
      ); // clear the reservations array
      updateUserQuery(cache, { user: { nextOneTimeProgram: { ...oneTimeProgram } } }, errors);
    },
  });

  const onServerError = (error) => {
    const errors = flatten([error]);
    displayApolloResponseErrors(errors);
  };

  const [createSplitTestAssignment] = useMutation(CREATE_SPLIT_TEST_ASSIGNMENT, {
    onCompleted: async (data) => {
      // eslint-disable-next-line no-console
      console.log('createSplitTestAssignment', data);
      const { createSplitTestAssignment: { backgroundJob } } = data;
      // eslint-disable-next-line no-console
      console.log('backgroundJob.id', backgroundJob?.id);
      setBackgroundJobId(backgroundJob?.id);
    },
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error('Error in setting bloodwork markers:', error);
    },
  });

  const handleCreateSplitTestAssignment = async (oneTimeProgramId) => {
    try {
      await createSplitTestAssignment({
        variables: {
          input: {
            oneTimeProgramId: oneTimeProgramId,
          },
        },
      });
    } catch (error) {
      console.error('Failed to create split test assignment:', error);
      throw error;
    }
  };

  // eslint-disable-next-line no-console
  const onCreateOneTimeProgramSuccess = (oneTimeProgram) => {
    // eslint-disable-next-line no-console
    console.log('oneTimeProgram', oneTimeProgram);
    setOneTimeProgramId(oneTimeProgram?.id);
    // eslint-disable-next-line no-console
    console.log('next', oneTimeProgram?.id);
    handleCreateSplitTestAssignment(oneTimeProgram?.id);
    // goPhase(7);
  };

  const onSubmitOneTimeProgram = () => {
    const input = {
      programBundleId: bundleId,
      numWeeks: 1,
      startDayId: firstPeriod?.id,
      otpSubscribed: true,
    };

    createOneTimeProgram({ variables: { input } })
      .then(
        ({
          data: {
            createOneTimeProgram: { oneTimeProgram, errors = [] },
          },
        }) => {
          errors.length ? onServerError(errors) : onCreateOneTimeProgramSuccess(oneTimeProgram);
        },
      )
      .catch(({ errors = [] }) => {
        if (errors.length) {
          onServerError(errors);
        }
      });
  };

  // eslint-disable-next-line no-console
  console.log('selectedProgram', selectedProgram);
  // eslint-disable-next-line no-console
  console.log('programSize', programSize);
  // eslint-disable-next-line no-console
  console.log('programMealTypesList', programMealTypesList);
  // eslint-disable-next-line no-console
  console.log('mealBundle', mealBundle);
  // eslint-disable-next-line no-console
  console.log('programSchedules', programSchedules);
  // eslint-disable-next-line no-console
  console.log('daysOfFoodList', daysOfFoodList);
  // eslint-disable-next-line no-console
  console.log('selectedDiet', selectedDiet);
  // eslint-disable-next-line no-console
  console.log('bundleId', bundleId);

  useEffect(() => {
    if (isEmpty(mealBundle)) {
      // eslint-disable-next-line no-console
      console.log('useEffect programMealTypesList', programMealTypesList);
      // eslint-disable-next-line no-console
      console.log('useEffect isEmpty', head(programMealTypesList));
      setMealBundle(head(programMealTypesList));
    }
  }, [mealBundle, programMealTypesList, setMealBundle]);

  const onSuccess = () => {
    // eslint-disable-next-line no-console
    console.log('Success block:', oneTimeProgramId);
    // eslint-disable-next-line no-console
    console.log('!isEmpty(oneTimeProgramId)', !isEmpty(oneTimeProgramId));
    setLoading(false);
    setBackgroundJobId(null);

    if (!isEmpty(oneTimeProgramId)) {
      // eslint-disable-next-line no-console
      console.log('entered goPhase');
      goPhase(7);
    }
  };

  const onError = () => {
    // eslint-disable-next-line no-console
    console.log('Error');
  };

  const progLabel = selectedProgram === 'sustain'
    ? 'Sustain GLP-1' : selectedProgram === 'peak'
    ? 'Peak' : 'Signature';
  const goalsWord = goals.length > 1 ? 'goals' : 'goal';
  const cuisinePrefsWord = cuisines.length > 1 ? 'prefs' : 'pref';
  const proteinPrefsWord = proteins.length > 1 ? 'prefs' : 'pref';
  const avoidancesWord = avoidances.length > 1 ? 'avoidances' : 'avoidance';
  const flagCountValue = Array.isArray(flagCount) ? flagCount.length : flagCount || 0;
  const flagWord = flagCountValue > 1 ? 'biomarkers' : 'biomarker';

  // ═══════════════════════════════════════════════════
  // LOADING STATE
  // ═══════════════════════════════════════════════════
  if (loading) {
    return (
      <>
        <LoadingScreen
          flagCount={flagCount}
          cuisines={cuisines}
          dots={dots}
          goals={goals}
        />
        <BackgroundJobStatusCheck
          backgroundJobId={backgroundJobId}
          onSuccess={onSuccess}
          onError={onError}
        />
      </>
    );
  }

  return (
    <>
      <Heading>Configure your plan</Heading>
      <Sub>Choose how many meals per week. You can change this anytime.</Sub>

      {/* Program badge */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          background: C.optimalBg,
          border: `1px solid ${C.green}`,
          borderRadius: 0,
          padding: '10px 14px',
          marginBottom: 20,
        }}
      >
        <Icon
          name={
            selectedProgram === 'sustain'
              ? 'shield'
              : selectedProgram === 'peak'
                ? 'mountain'
                : 'sparkle'
          }
          size={18}
          color={C.green}
        />
        <div style={{ flex: 1 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: C.darkBrown }}>
            {progLabel} · {programSize?.name}
          </span>
          <span style={{ fontSize: 11, color: C.brown, marginLeft: 8 }}>
            {specs.prot} protein · {specs.cal} cal per meal
          </span>
        </div>
        <span
          style={{ fontSize: 11, fontWeight: 600, color: C.green, cursor: 'pointer' }}
          onClick={() => goPhase(5, 0)}
        >
          Change
        </span>
      </div>

      {/* Which Meals */}
      <div
        style={{
          fontFamily: F.h,
          fontSize: 20,
          fontWeight: 500,
          color: C.darkBrown,
          marginBottom: 12,
        }}
      >
        Which Meals?
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
        {programMealTypesList &&
          programMealTypesList.map((o) => (
            <div
              key={o.id}
              onClick={() => { setBundleId(null); setMealBundle(o); }}
              style={{
                padding: '14px 16px',
                background: mealBundle?.id === o.id ? C.optimalBg : C.pureWhite,
                border: `${mealBundle?.id === o.id ? '2px' : '1px'} solid ${mealBundle?.id === o.id ? C.green : '#E8E4DC'}`,
                borderRadius: 0,
                cursor: 'pointer',
                textAlign: 'center',
                fontSize: 13,
                fontWeight: 500,
                color: C.darkBrown,
                lineHeight: 1.5,
              }}
            >
              <SanitizedHTML rawHTML={o.description} />
              {o.bold && <span style={{ fontWeight: 700 }}> ({o.bold})</span>}
            </div>
          ))}
      </div>

      {/* Days of Food */}
      <div
        style={{
          fontFamily: F.h,
          fontSize: 20,
          fontWeight: 500,
          color: C.darkBrown,
          marginBottom: 12,
        }}
      >
        Days of Food
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: daysOfFoodList?.length === 1 ? 8 : 24 }}>
        {daysOfFoodList && daysOfFoodList.map((o) => (
          <div
            key={o.id}
            onClick={() => { setBundleId(null); setDaysOfFood(o); }}
            style={{
              padding: '16px 12px',
              background: daysOfFood?.id === o.id ? C.optimalBg : C.pureWhite,
              border: `${daysOfFood?.id === o?.id ? '2px' : '1px'} solid ${daysOfFood?.id === o?.id ? C.green : '#E8E4DC'}`,
              borderRadius: 0,
              cursor: 'pointer',
              textAlign: 'center',
              fontSize: 14,
              fontWeight: 600,
              color: C.darkBrown,
            }}
          >
            {o?.name} | {`${head(o?.daysOfWeek)?.name?.slice(0, 3)} - ${last(o?.daysOfWeek)?.name?.slice(0, 3)}`}
          </div>
        ))}
      </div>
      {daysOfFoodList?.length === 1 && (
        <div style={{ fontSize: 12, color: C.brown, fontStyle: 'italic', marginBottom: 24 }}>
          This meal configuration is available for 5-day delivery only.
        </div>
      )}

      {/* Subscription note */}
      <div style={{ fontSize: 12, color: C.brown, lineHeight: 1.5, marginBottom: 16 }}>
        This is a weekly subscription. A new menu every week, charged weekly. Skip any week or
        cancel anytime with one week's notice.
      </div>

      {/* Plan summary */}
      <Callout>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.darkBrown, marginBottom: 4 }}>
          {progLabel} · {sizeLabel} · {daysOfFood?.name}/week · <SanitizedHTML rawHTML={mealBundle?.description} />
        </div>
        <div style={{ fontSize: 11, color: C.brown, lineHeight: 1.5 }}>
          {specs.prot} protein per lunch/dinner meal · {specs.cal} cal per meal
        </div>
        <div style={{ fontSize: 11, color: C.brown }}>
          {goals.length} health {goalsWord} ·
          {cuisines.length} cuisine {cuisinePrefsWord} · {proteins.length} protein {proteinPrefsWord} · {avoidances.length} {avoidancesWord} · {flagCountValue} {flagWord} flagged · {household || 1}{' '}
          person
        </div>
      </Callout>

      <Btn
        disabled={isEmpty(bundleId)}
        onClick={() => {
          onSubmitOneTimeProgram();
          setLoading(true);
        }}
        style={{ width: '100%' }}
      >
        Generate My Personalized Menu <ChevronRight size={15} />
      </Btn>
    </>
  );
};

export default Phase6PlanConfig;
